import React from 'react';
import ParticlesBG from '../../Shared/ParticlesBG';
import '../../Assets/Stylesheets/Pages/Legal/Legal.scss';

export default function TermsAndConditions() {
  const lastUpdated = "January 20, 2026";
  const companyName = "TRND";
  const companyEmail = "legal@trnd.co";

  return (
    <div className="legal-page">
      <ParticlesBG />
      <div className="legal-content">
        <h1>Terms and Conditions</h1>
        <p className="last-updated">Last Updated: {lastUpdated}</p>

        <section className="legal-section">
          <h2>1. Agreement to Terms</h2>
          <p>
            These Terms and Conditions ("Terms") constitute a legally binding agreement between 
            you ("User," "you," or "your") and {companyName} ("Company," "we," "us," or "our") 
            governing your access to and use of the {companyName} website, platform, and services 
            (collectively, the "Services").
          </p>
          <p>
            By accessing or using our Services, you agree to be bound by these Terms. If you 
            disagree with any part of these Terms, you may not access the Services.
          </p>
        </section>

        <section className="legal-section">
          <h2>2. Description of Services</h2>
          <p>{companyName} provides an AI-powered marketing platform that includes:</p>
          <ul>
            <li><strong>Marketing Genius:</strong> AI-powered marketing strategy and planning tool</li>
            <li><strong>AI Content Tools:</strong> Blog generators, social media post creators, email generators, and more</li>
            <li><strong>Marketplace:</strong> Connection platform between customers and marketing service vendors</li>
            <li><strong>Corporate Products:</strong> Branded merchandise and promotional items</li>
            <li><strong>Subscription Services:</strong> Various tiers of access to platform features</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>3. User Accounts</h2>
          
          <h3>3.1 Account Registration</h3>
          <p>
            To access certain features of the Services, you must register for an account. 
            You agree to provide accurate, current, and complete information during registration 
            and to update such information to keep it accurate, current, and complete.
          </p>
          
          <h3>3.2 Account Security</h3>
          <p>
            You are responsible for safeguarding your password and for all activities that 
            occur under your account. You must notify us immediately of any unauthorized use 
            of your account.
          </p>
          
          <h3>3.3 Account Termination</h3>
          <p>
            We reserve the right to suspend or terminate your account at any time for any 
            reason, including if we reasonably believe you have violated these Terms.
          </p>
        </section>

        <section className="legal-section">
          <h2>4. Subscription Plans and Payments</h2>
          
          <h3>4.1 Subscription Tiers</h3>
          <p>
            We offer various subscription tiers (Free, Basic, Standard, Pro, and Agency plans) 
            with different features and pricing. Current pricing and features are available on 
            our pricing page.
          </p>
          
          <h3>4.2 Billing</h3>
          <p>
            Paid subscriptions are billed in advance on a monthly or annual basis. All prices 
            are in Canadian Dollars (CAD) unless otherwise specified. Prices may be converted 
            to USD or other currencies for display purposes.
          </p>
          
          <h3>4.3 Automatic Renewal</h3>
          <p>
            Subscriptions automatically renew unless cancelled before the renewal date. You 
            can cancel your subscription at any time through your account settings or by 
            contacting us.
          </p>
          
          <h3>4.4 Price Changes</h3>
          <p>
            We reserve the right to modify subscription prices. We will provide at least 30 
            days' notice before any price increase takes effect for your subscription.
          </p>
          
          <h3>4.5 Tokens and Usage Limits</h3>
          <p>
            Certain features are subject to usage limits based on your subscription tier. 
            Token allocations reset monthly on your billing date. Unused tokens do not roll 
            over unless specified in your plan.
          </p>
        </section>

        <section className="legal-section">
          <h2>5. Refund Policy</h2>
          <p>
            For detailed information about refunds, please see our{' '}
            <a href="/refund-policy">Refund Policy</a>. In summary:
          </p>
          <ul>
            <li>Subscription fees are generally non-refundable</li>
            <li>Refunds may be considered on a case-by-case basis within 14 days of purchase</li>
            <li>Marketplace services are subject to separate refund terms with vendors</li>
            <li>Corporate product orders may be eligible for refunds per our return policy</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>6. Acceptable Use Policy</h2>
          <p>You agree not to use the Services to:</p>
          <ul>
            <li>Violate any applicable laws or regulations</li>
            <li>Infringe on intellectual property rights of others</li>
            <li>Generate illegal, harmful, or offensive content</li>
            <li>Harass, abuse, or harm other users or vendors</li>
            <li>Distribute malware, spam, or other malicious content</li>
            <li>Attempt to gain unauthorized access to our systems</li>
            <li>Interfere with or disrupt the Services</li>
            <li>Use automated systems (bots, scrapers) without permission</li>
            <li>Resell or redistribute the Services without authorization</li>
            <li>Misrepresent your identity or affiliation</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>7. AI-Generated Content</h2>
          
          <h3>7.1 Ownership</h3>
          <p>
            You retain ownership of the content you create using our AI tools, subject to 
            these Terms. You grant us a license to use, store, and process your content as 
            necessary to provide the Services.
          </p>
          
          <h3>7.2 Accuracy Disclaimer</h3>
          <p>
            AI-generated content is provided "as is" without warranties of accuracy, 
            completeness, or fitness for any particular purpose. You are responsible for 
            reviewing and editing AI-generated content before use.
          </p>
          
          <h3>7.3 Prohibited Content</h3>
          <p>
            You may not use our AI tools to generate content that is illegal, defamatory, 
            discriminatory, sexually explicit, violent, or otherwise harmful.
          </p>
          
          <h3>7.4 Third-Party AI Services</h3>
          <p>
            Our AI features utilize third-party services (OpenAI). Your use of these features 
            is also subject to their terms of service.
          </p>
        </section>

        <section className="legal-section">
          <h2>8. Marketplace Terms</h2>
          
          <h3>8.1 Platform Role</h3>
          <p>
            {companyName} acts as a platform connecting customers with independent marketing 
            service vendors. We are not a party to agreements between customers and vendors.
          </p>
          
          <h3>8.2 Vendor Services</h3>
          <p>
            We do not guarantee the quality, timeliness, or outcomes of services provided by 
            vendors. Disputes between customers and vendors should be resolved directly, 
            though we may assist in mediation.
          </p>
          
          <h3>8.3 Platform Fees</h3>
          <p>
            We charge platform fees on marketplace transactions. These fees are disclosed 
            before purchase and are non-refundable.
          </p>
        </section>

        <section className="legal-section">
          <h2>9. Intellectual Property</h2>
          
          <h3>9.1 Our Content</h3>
          <p>
            The Services, including the website, platform, software, design, text, graphics, 
            logos, and other materials, are owned by {companyName} and protected by copyright, 
            trademark, and other intellectual property laws.
          </p>
          
          <h3>9.2 Limited License</h3>
          <p>
            We grant you a limited, non-exclusive, non-transferable license to access and 
            use the Services for your personal or business use in accordance with these Terms.
          </p>
          
          <h3>9.3 User Content</h3>
          <p>
            You retain ownership of content you upload to the Services. By uploading content, 
            you grant us a worldwide, royalty-free license to use, store, and display your 
            content as necessary to provide the Services.
          </p>
        </section>

        <section className="legal-section">
          <h2>10. Limitation of Liability</h2>
          <p>
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, {companyName.toUpperCase()} SHALL NOT BE 
            LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, 
            INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, USE, GOODWILL, OR OTHER 
            INTANGIBLE LOSSES, RESULTING FROM:
          </p>
          <ul>
            <li>Your access to or use of (or inability to access or use) the Services</li>
            <li>Any conduct or content of third parties on the Services</li>
            <li>Any content obtained from the Services</li>
            <li>Unauthorized access, use, or alteration of your transmissions or content</li>
          </ul>
          <p>
            OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID TO US IN THE TWELVE (12) 
            MONTHS PRECEDING THE CLAIM.
          </p>
        </section>

        <section className="legal-section">
          <h2>11. Disclaimer of Warranties</h2>
          <p>
            THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY 
            KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES 
            OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
          </p>
          <p>
            WE DO NOT WARRANT THAT THE SERVICES WILL BE UNINTERRUPTED, ERROR-FREE, OR 
            COMPLETELY SECURE. WE DO NOT WARRANT THAT THE RESULTS OBTAINED FROM USING THE 
            SERVICES, INCLUDING AI-GENERATED CONTENT, WILL BE ACCURATE OR RELIABLE.
          </p>
        </section>

        <section className="legal-section">
          <h2>12. Indemnification</h2>
          <p>
            You agree to indemnify, defend, and hold harmless {companyName}, its officers, 
            directors, employees, agents, and affiliates from and against any claims, 
            liabilities, damages, losses, costs, or expenses (including reasonable attorneys' 
            fees) arising out of or relating to:
          </p>
          <ul>
            <li>Your use of the Services</li>
            <li>Your violation of these Terms</li>
            <li>Your violation of any rights of another party</li>
            <li>Content you create or upload to the Services</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>13. Dispute Resolution</h2>
          
          <h3>13.1 Informal Resolution</h3>
          <p>
            Before initiating any legal proceeding, you agree to contact us at {companyEmail} 
            to attempt to resolve the dispute informally.
          </p>
          
          <h3>13.2 Governing Law</h3>
          <p>
            These Terms shall be governed by and construed in accordance with the laws of 
            Canada, without regard to its conflict of law provisions.
          </p>
          
          <h3>13.3 Jurisdiction</h3>
          <p>
            You agree to submit to the exclusive jurisdiction of the courts located in 
            Canada for the resolution of any disputes arising from these Terms or the Services.
          </p>
        </section>

        <section className="legal-section">
          <h2>14. Changes to Terms</h2>
          <p>
            We reserve the right to modify these Terms at any time. We will notify you of 
            any material changes by posting the new Terms on this page and updating the 
            "Last Updated" date. Your continued use of the Services after any changes 
            constitutes acceptance of the new Terms.
          </p>
        </section>

        <section className="legal-section">
          <h2>15. Severability</h2>
          <p>
            If any provision of these Terms is held to be invalid or unenforceable, the 
            remaining provisions shall continue in full force and effect.
          </p>
        </section>

        <section className="legal-section">
          <h2>16. Entire Agreement</h2>
          <p>
            These Terms, together with our Privacy Policy, Cookie Policy, and Refund Policy, 
            constitute the entire agreement between you and {companyName} regarding the 
            Services and supersede all prior agreements.
          </p>
        </section>

        <section className="legal-section">
          <h2>17. Contact Information</h2>
          <p>If you have any questions about these Terms, please contact us:</p>
          <ul className="contact-info">
            <li><strong>Email:</strong> {companyEmail}</li>
            <li><strong>Website:</strong> <a href="/">www.trnd.co</a></li>
          </ul>
        </section>
      </div>
    </div>
  );
}
